import IconExpandLess from "@mui/icons-material/ExpandLess";
import { Box, Button, IconButton } from "@mui/material";

type MapButtonProps = {
    onClick: () => void;
    icon?: React.ReactNode;
    label: string;
    caption?: string;
    active?: boolean;
    onChevronClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
    isChevronOpen?: boolean;
    chevronLabel?: string;
};

export function MapButton({
    onClick,
    icon,
    label,
    caption,
    active,
    onChevronClick,
    isChevronOpen = false,
    chevronLabel,
}: MapButtonProps) {
    return (
        <Box
            sx={theme => ({
                boxShadow: "var(--Paper-shadow)",
                border: `1px solid ${theme.palette.divider}`,
                backgroundColor: active ? theme.palette.primary.main : theme.palette.background.paper,
                marginLeft: "-1px",
                overflow: "hidden",
                "&:first-of-type": {
                    borderTopLeftRadius: "40px",
                    borderBottomLeftRadius: "40px",
                },
                "&:last-of-type": {
                    borderTopRightRadius: "40px",
                    borderBottomRightRadius: "40px",
                },
                "&:hover": {
                    backgroundColor: active ? theme.palette.primary.dark : theme.palette.background.default,
                },
            })}
        >
            <Button
                onClick={onClick}
                sx={theme => ({
                    color: active ? theme.palette.primary.contrastText : theme.palette.text.primary,
                    gap: 1,
                    paddingInline: 2,
                    paddingInlineEnd: onChevronClick ? 1 : 2,
                    textTransform: "none",
                    fontWeight: "bold",
                    lineHeight: 1.2,
                    paddingBlock: caption ? 0.5 : 1,
                })}
            >
                {icon}
                {caption ? (
                    <Box
                        component="span"
                        sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", minWidth: 0 }}
                    >
                        <span>{label}</span>
                        <Box
                            component="span"
                            sx={theme => ({
                                ...theme.typography.caption,
                                lineHeight: 1.2,
                                color: active ? "inherit" : theme.palette.text.secondary,
                                maxWidth: "125px",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                            })}
                        >
                            {caption}
                        </Box>
                    </Box>
                ) : (
                    label
                )}
            </Button>
            {onChevronClick && (
                <IconButton
                    size="small"
                    onClick={onChevronClick}
                    aria-label={chevronLabel}
                    aria-haspopup="true"
                    aria-expanded={isChevronOpen}
                    sx={theme => ({
                        color: theme.palette.text.primary,
                        minWidth: 0,
                        marginInlineEnd: 1,
                        "& svg": {
                            transform: isChevronOpen ? "rotate(180deg)" : "none",
                            transition: theme.transitions.create("transform", {
                                duration: theme.transitions.duration.shorter,
                            }),
                        },
                    })}
                >
                    <IconExpandLess />
                </IconButton>
            )}
        </Box>
    );
}
